/**
 * Глобальный поиск.
 *
 * §3.5: ищет по задачам, сотрудникам и проектам во ВСЕХ разрешённых
 * подразделениях, а не только по текущей доске. Это частая ошибка:
 * поиск, ограниченный открытой доской, бесполезен.
 *
 * §29.3: SEARCH_MIN_LENGTH = 2 — за порог отвечает SearchInput.
 *
 * Два вида (docs/08-mobile-roadmap.md, M1): на десктопе — поле в шапке с
 * выпадающими результатами; на телефоне и планшете — кнопка, открывающая
 * поиск на весь экран. Слой закрывается кнопкой «Назад» браузера.
 */

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { ArrowLeft, FolderKanban, ListTodo, Search, Users } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SEARCH_GROUP_LABELS, useGlobalSearchQuery } from "@taskflow/client-core/search";
import { useHistoryOverlay } from "@/shared/lib/historyOverlay";
import { cn, IconButton, SearchInput } from "@/shared/ui";

const ICONS = {
  task: ListTodo,
  user: Users,
  project: FolderKanban,
} as const;

const GROUP_LABELS = SEARCH_GROUP_LABELS;

const LABEL = "Глобальный поиск по задачам, сотрудникам и проектам";

export function GlobalSearch() {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");

  return (
    <div className="relative max-w-md">
      <SearchInput
        label={LABEL}
        placeholder="Поиск по всем доступным подразделениям"
        value={input}
        onChange={setInput}
        onSearch={setQuery}
      />

      {query.length >= 2 && (
        <div className="absolute top-full right-0 left-0 z-40 mt-1 max-h-96 overflow-y-auto rounded-md border border-line bg-raised p-1 shadow-md">
          <SearchResults
            query={query}
            onPick={(url) => {
              setInput("");
              setQuery("");
              void navigate(url);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Кнопка поиска для телефона и планшета и сам полноэкранный слой. */
export function GlobalSearchButton({ className }: { className?: string }) {
  const overlay = useHistoryOverlay("globalSearch");

  return (
    <>
      <IconButton
        icon={Search}
        label="Открыть поиск"
        onClick={overlay.show}
        className={className}
      />
      {/* Только открытым: закрытый Headless UI Dialog в шапке мешал
          другим диалогам — карточка задачи переставала делать доску под
          собой недоступной (inert), и доска читалась сквозь карточку. */}
      {overlay.open && (
        <Dialog
          open
          onClose={overlay.hide}
          // Фокус ставит SearchOverlayBody; свой автофокус Headless UI
          // перебил бы его, переведя фокус на сам диалог.
          autoFocus={false}
          className="relative z-50"
        >
          <DialogPanel className="fixed inset-0 flex flex-col bg-surface pt-(--safe-top) pr-(--safe-right) pb-(--safe-bottom) pl-(--safe-left)">
            <DialogTitle className="sr-only">Поиск</DialogTitle>
            {/* Состояние поиска живёт внутри панели: закрытый слой
                размонтируется, и следующий поиск начинается с чистого поля. */}
            <SearchOverlayBody onClose={overlay.hide} />
          </DialogPanel>
        </Dialog>
      )}
    </>
  );
}

function SearchOverlayBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const bar = useRef<HTMLDivElement>(null);

  // Фокус в поле ставится сам: на сенсорных устройствах Headless UI
  // начальный фокус намеренно не ставит, а слой поиска без поля в фокусе
  // требует лишнего касания. Эффект разметки, а не обычный: он выполняется
  // в том же обработчике нажатия, и iOS соглашается открыть клавиатуру.
  useLayoutEffect(() => {
    bar.current?.querySelector("input")?.focus();
  }, []);

  return (
    <>
      <div ref={bar} className="flex shrink-0 items-start gap-2 border-b border-line px-3 py-2.5">
        <IconButton icon={ArrowLeft} label="Закрыть поиск" onClick={onClose} />
        <SearchInput
          label={LABEL}
          placeholder="Задачи, сотрудники, проекты"
          value={input}
          onChange={setInput}
          onSearch={setQuery}
          className="min-w-0 flex-1"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {query.length >= 2 ? (
          <SearchResults
            query={query}
            large
            // replace: запись слоя в истории заменяется результатом, и
            // «Назад» с результата ведёт туда, где поиск открывали.
            onPick={(url) => void navigate(url, { replace: true })}
          />
        ) : (
          <p className="px-3 py-3 text-[13px] text-faint">
            Поиск по всем доступным подразделениям
          </p>
        )}
      </div>
    </>
  );
}

function SearchResults({
  query,
  onPick,
  large = false,
}: {
  query: string;
  onPick: (url: string) => void;
  /** Строки под палец: на телефоне цель касания — не меньше 44 px. */
  large?: boolean;
}) {
  const results = useGlobalSearchQuery(query);

  const grouped = (["task", "user", "project"] as const)
    .map((kind) => ({
      kind,
      items: (results.data?.data ?? []).filter((hit) => hit.kind === kind),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      {results.isFetching && <p className="px-3 py-3 text-[13px] text-faint">Ищем…</p>}

      {!results.isFetching && grouped.length === 0 && (
        <p className="px-3 py-3 text-[13px] text-faint">Ничего не найдено</p>
      )}

      {grouped.map((group) => (
        <div key={group.kind}>
          <p className="px-2.5 pt-2 pb-1 text-[10px] font-bold tracking-wide text-faint uppercase">
            {GROUP_LABELS[group.kind]}
          </p>
          {group.items.map((hit) => {
            const Icon = ICONS[hit.kind];
            return (
              <button
                key={`${hit.kind}-${hit.id}`}
                type="button"
                onClick={() => onPick(hit.url)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-sm px-2.5 text-left hover:bg-sunken",
                  large ? "min-h-11 py-2" : "py-1.5",
                )}
              >
                <Icon className="size-4 shrink-0 text-faint" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-fg">{hit.title}</span>
                  <span className="block truncate text-[11px] text-faint">{hit.subtitle}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}
